import { RiskLevel, VerificationStatus } from "../../../../types/cdd-records.types";

/**
 * The single label vocabulary for roles (AL-519 item 6).
 *
 * Four renderers display roles — this panel, the record-detail view, and the
 * PDF/CSV/JSON exports. Before AL-519 each had its own convention, so one id
 * could read "Application Contact" on screen, `C2_USER` on the detail view and
 * `Application-contact` in the PDF. They all resolve through this map now.
 *
 * `authorised-representative` is deliberately GONE rather than aliased. It meant
 * two unrelated things and a C1 read it as "authorised to act"; leaving an alias
 * would let a caller keep using it silently. Anything still mapping to it will
 * now fall through to the caller's own fallback, which is visible.
 */
export const roleLabels: Record<string, string> = {
	director: "Director",
	"beneficial-owner": "Beneficial Owner",
	// AL-519 item 7: the same UBO role, named correctly for entity types that do
	// not have beneficial owners in the ASIC sense.
	"senior-managing-official": "Senior Managing Official",
	// AL-519 item 1: split out of the old shared "Authorised Representative".
	"approved-user": "Approved User",
	"application-contact": "Application Contact",
	shareholder: "Shareholder",
	secretary: "Secretary",
	trustee: "Trustee",
	beneficiary: "Beneficiary",
	settlor: "Settlor",
	appointer: "Appointer",
	nominee: "Nominee",
	"authorised-signatory": "Authorised Signatory",
	partner: "Partner",
	"committee-member": "Committee Member",
	"office-bearer": "Office Bearer",
	"key-controller": "Key Controller",
	other: "Other",
};

/**
 * Resolve a mapped role id to its display label (AL-519 item 6).
 *
 * Use this rather than indexing `roleLabels` directly, and never
 * `capitalize(id)` — that is what rendered `application-contact` as
 * "Application-contact" in the PDF while the screen said "Application Contact".
 *
 * The fallback de-hyphenates and title-cases, so an id added to the mapper but
 * not to `roleLabels` degrades to something readable instead of appearing raw.
 * It is a safety net, not a licence to skip the map.
 */
export const roleLabel = (role: string): string =>
	roleLabels[role] ||
	role.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const roleColorClass: Record<string, string> = {
	director: "bg-blue-100 text-blue-800 ring-blue-200",
	"beneficial-owner": "bg-purple-100 text-purple-800 ring-purple-200",
	// Same purple as beneficial-owner: it is the same underlying role, renamed for
	// the entity type, so colouring it differently would imply a different fact.
	"senior-managing-official": "bg-purple-100 text-purple-800 ring-purple-200",
	// Distinct colours, because the whole point is that these are not the same
	// thing. Sharing indigo would undo the split visually.
	"approved-user": "bg-indigo-100 text-indigo-800 ring-indigo-200",
	"application-contact": "bg-slate-100 text-slate-700 ring-slate-300",
	shareholder: "bg-emerald-100 text-emerald-800 ring-emerald-200",
	secretary: "bg-teal-100 text-teal-800 ring-teal-200",
	trustee: "bg-amber-100 text-amber-800 ring-amber-200",
	beneficiary: "bg-cyan-100 text-cyan-800 ring-cyan-200",
	settlor: "bg-orange-100 text-orange-800 ring-orange-200",
	appointer: "bg-rose-100 text-rose-800 ring-rose-200",
	nominee: "bg-pink-100 text-pink-800 ring-pink-200",
	"authorised-signatory": "bg-violet-100 text-violet-800 ring-violet-200",
	partner: "bg-sky-100 text-sky-800 ring-sky-200",
	"committee-member": "bg-lime-100 text-lime-800 ring-lime-200",
	"office-bearer": "bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200",
	"key-controller": "bg-red-100 text-red-800 ring-red-200",
	other: "bg-slate-100 text-slate-600 ring-slate-200",
};

export const getRiskBadgeClass = (risk?: RiskLevel): string => {
	switch (risk) {
		case "low":
			return "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 ring-1 ring-emerald-200";
		case "medium":
			return "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 ring-1 ring-amber-200";
		case "high":
			return "bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 ring-1 ring-orange-200";
		case "critical":
			return "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 ring-1 ring-rose-200";
		default:
			return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 ring-1 ring-slate-200";
	}
};

export const getVerificationBadgeClass = (status: VerificationStatus): string => {
	switch (status) {
		case "verified":
			return "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 ring-1 ring-emerald-200";
		case "pending":
			return "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 ring-1 ring-amber-200";
		case "failed":
			return "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 ring-1 ring-rose-200";
		case "expired":
			return "bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 ring-1 ring-orange-200";
		case "not-started":
			return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 ring-1 ring-slate-200";
		default:
			return "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 ring-1 ring-slate-200";
	}
};
